import { DeployMicroserviceRequest, GetDeployedMicroservicesInStageResponseDeployedMicroservices, GetDeployedMicroservicesInStageResponseMicroservices, GetMicroservicesResponseMicroservices } from "@microtica/ms-kube-sdk";
import { Alert, Avatar, Button, Card, Col, Dropdown, notification, Row, Select, Space, Table, Tag } from "antd";
import moment from "moment-timezone";
import { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom";
import { MoreOutlined, DownOutlined, PlusCircleOutlined, ThunderboltOutlined } from '@ant-design/icons';
import { getElasticSearchService, getKubeService } from "../../backend";
import CardMarketing from "../../components/marketing/CardMarketing";
import DeploymentsImage from "../../assets/deployments-marketing.svg";
import Heading from "../../components/Heading";
import "./index.css";
import { useDoubleConfigurationModal } from "../../contexts/Modal";
import DeployDifferentApplicationVersionModal from "../../components/modals/DeployDifferentApplicationVersionModal";
import { useRecoilValue } from "recoil";
import { currentProjectState } from "../../recoil/project";
import TemplateAvatar from "../../components/TemplateAvatar";
import GitCommitLink from "../../components/GitCommitLink";
import LimitationReachedMessage from "../../components/LimitationReachedMessage";
import { fetchProjectLimitations } from "../../utils/local-storage";
import PodsStats from "../../components/PodsStatus";
import SegmentedItems from "../../components/SegmentedItems";
import { IEnvDetails } from "../../types";
const { Option } = Select;

const EnvironmentApps = () => {
    const { projectId, envId } = useParams();
    const navigate = useNavigate();
    const { envDetails } = useOutletContext<{ envDetails: IEnvDetails }>();
    const currentProject = useRecoilValue(currentProjectState);
    const [loading, setLoading] = useState(true);
    const [showDeployDifferentApplicationVersionModal, setShowDeployDifferentApplicationVersionModal] = useState(false);
    const [chosenService, setChosenService] = useState<GetMicroservicesResponseMicroservices>();
    const [deployedServices, setDeployedServices] = useState<GetDeployedMicroservicesInStageResponseDeployedMicroservices[]>([]);
    const [filteredDeployedServices, setFilteredDeployedServices] = useState<GetDeployedMicroservicesInStageResponseDeployedMicroservices[]>([]);
    const { open: openConfirmModal } = useDoubleConfigurationModal();
    const [namespacesInUse, setNamespacesInUse] = useState<string[]>([]);
    const [selectedNamespaces, setSelectedNamespaces] = useState<string[]>([]);
    const [disableAddApp, setDisableAddApp] = useState<boolean>(false);
    const [numOfDeployedServices, setNumOfDeployedServices] = useState<number>(0);
    const { kubernetesServices } = fetchProjectLimitations();

    const loadData = async () => {
        const commitMap: { [serviceName: string]: object } = await fetchServicesCommitInfo();
        setLoading(false);

        const sortedDeployedMicroservices = envDetails.deployedMicroservices
            .sort((a, b) => a.namespace < b.namespace ? -1 : a.namespace > b.namespace ? 1 : 0)
            .map(item => {
                return {
                    ...item,
                    microservices: item.microservices.map(svc => {
                        return {
                            ...svc,
                            commit: commitMap[svc.name]
                        }
                    })
                }
            });
        setDeployedServices(sortedDeployedMicroservices);

        setNamespacesInUse(sortedDeployedMicroservices.map(service => service.namespace).filter((v, i, a) => a.indexOf(v) === i));

        setNumOfDeployedServices(sortedDeployedMicroservices.reduce((sum, d) => { return sum + d.microservices.length }, 0))

        // redirect user to other page if there are no deployed services to be displayed
        if (envDetails.deployedMicroservices.length === 0) {
            navigate(`/projects/${projectId}/environments/${envId}/apps/new`, { state: { redirectToAppsPage: true } });
        }

    }

    useEffect(() => {
        if (envDetails) {
            loadData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [envDetails.deployedMicroservices]);

    useEffect(() => {
        // Limitations message logic
        if (kubernetesServices <= numOfDeployedServices) {
            setDisableAddApp(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [numOfDeployedServices]);

    const fetchServicesCommitInfo = async () => {
        try {
            const { data: { kuberneteses } } = await getKubeService().listKubernetes(projectId!);
            const clusters = kuberneteses.filter(kube => kube.id.startsWith(envId!));

            let commitMap: { [serviceName: string]: object } = {}
            await Promise.all(clusters.map(async cluster => {
                const { data: { response } } = await getElasticSearchService().getDeployedMicroservices(projectId!, cluster.id);
                commitMap = response
                    .reduce((prev, curr) => {
                        return {
                            ...prev,
                            [curr.name]: curr.commit
                        }
                    }, commitMap);
            }));
            return commitMap;
        } catch (e) {
            return {};
        }
    }

    const handleDeleteApplication = async (service: GetDeployedMicroservicesInStageResponseMicroservices) => {
        try {
            const { data: response } = await getKubeService().undeployMicroservice(
                service.name,
                service.namespace,
                service.kubernetesId,
                projectId!);

            if (response.done) {
                notification.success({
                    message: `Application deleted`,
                    description: `Application ${service.name} was successfully deleted`,
                });
                setLoading(true);
                envDetails.refreshRoot();
            }
        } catch (error) {
            notification.error({
                message: "Error deleting application",
                description: (error as any).response.data.message
            });
        }
    }

    const handleRestartApplication = async (service: GetDeployedMicroservicesInStageResponseMicroservices) => {
        try {
            const { data: { configurations } } = await getKubeService().getMicroserviceDeploymentStatus(
                service.name,
                service.kubernetesId,
                service.namespace,
                projectId!
            );

            const microserviceRequest: DeployMicroserviceRequest = {
                deployment: {
                    image: service.version,
                    configurations,
                    containerPort: parseInt(configurations.find(c => c.key === "MIC_CONTAINER_PORT")?.value || "80")
                },
                autoScaling: {
                    cpu: parseInt(configurations.find(c => c.key === "MIC_AS_CPU")!.value!),
                    maxCpu: parseInt(configurations.find(c => c.key === "MIC_AS_MAX_CPU")!.value!),
                    memory: parseInt(configurations.find(c => c.key === "MIC_AS_MEMORY")!.value!),
                    maxMemory: parseInt(configurations.find(c => c.key === "MIC_AS_MAX_MEMORY")!.value!),
                    cpuUtilization: 80,
                    memoryUtilization: 80,
                    minReplicas: parseInt(configurations.find(c => c.key === "MIC_AS_MIN_REPLICAS")!.value!),
                    maxReplicas: parseInt(configurations.find(c => c.key === "MIC_AS_MAX_REPLICAS")!.value!),
                }
            }

            await getKubeService().deployMicroservice(
                service.name,
                service.kubernetesId,
                service.namespace,
                projectId!,
                microserviceRequest
            );

            notification.success({
                message: "Restart initiated",
                description: "It would take a few moments to apply the changes."
            });
            loadData();
        } catch (error: any) {
            notification.error({
                message: "Restart failed",
                description: error.response.data.message
            });
        }
    }

    const showDeleteConfirm = (service: GetDeployedMicroservicesInStageResponseMicroservices) => {
        openConfirmModal({
            title: "Delete Application?",
            description:
                <Space direction='vertical'>
                    <div>The application will be permanently deleted, including its deployments and domains.</div>
                    <Alert message={<b>This action cannot be undone.</b>} type="error" />
                </Space>,
            confirmation: service.name,
            okText: "Delete Application",
            cancelText: 'Cancel',
            onOk: () => handleDeleteApplication(service)
        });
    }

    const showRestartConfirm = (service: GetDeployedMicroservicesInStageResponseMicroservices) => {
        openConfirmModal({
            title: 'Restart Application?',
            description:
                <Space direction='vertical'>
                    <div>The current application instances will be terminated and new instances will be created.</div>
                    <Alert message={<b>This action cannot be undone.</b>} type="error" />
                </Space>,
            confirmation: service.name,
            okText: "Restart Application",
            cancelText: 'Cancel',
            onOk: () => handleRestartApplication(service)
        });
    }

    const filterMicroservices = () => {
        selectedNamespaces.length === 0 ?
            setFilteredDeployedServices(deployedServices) :
            setFilteredDeployedServices(deployedServices.filter(({ namespace }) => selectedNamespaces.includes(namespace.toLowerCase())));
    }

    useEffect(() => {
        filterMicroservices();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedNamespaces, namespacesInUse])

    return (
        <>
            {
                <>
                    {
                        loading ?
                            <Table
                                loading={true}
                                pagination={false}
                                className="ant-table-wrapper-bordered application-table empty"
                                rowKey="id"
                                key="namespaces"
                            />
                            :
                            <>
                                <Row gutter={24}>
                                    <Col xs={24} sm={12} md={7}>
                                        <Heading
                                            title={<div className="flex-justify-space-between flex-align-center">
                                                <span>
                                                    <ThunderboltOutlined /> Live apps
                                                </span>
                                            </div>}
                                            description="Deployed and currently active applications"
                                            level={3}
                                        />
                                    </Col>
                                    <Col xs={24} sm={12} md={17} style={{ marginTop: 20, textAlign: "right" }}>
                                        <LimitationReachedMessage
                                            visible={disableAddApp}
                                            text={`${kubernetesServices} services`} />
                                        <Button
                                            disabled={disableAddApp}
                                            onClick={() => {
                                                navigate(`/projects/${projectId}/environments/${envId}/apps/new`);
                                            }}>
                                            <PlusCircleOutlined />
                                            Add app
                                        </Button>
                                    </Col>
                                </Row>

                                {
                                    !!namespacesInUse.length &&
                                    <div style={{ textAlign: "right" }}>
                                        <span style={{ marginRight: 10 }}>Filter by namespace:</span>
                                        <Select
                                            mode="multiple"
                                            allowClear
                                            style={{ minWidth: "35%", textAlign: "left" }}
                                            placeholder="Namespace"
                                            defaultValue={namespacesInUse}
                                            onChange={setSelectedNamespaces}
                                        >
                                            {namespacesInUse.filter(o => !selectedNamespaces.includes(o)).map(item => <Option key={item}>{item}</Option>)}
                                        </Select>
                                    </div>
                                }
                                <br />
                                {
                                    filteredDeployedServices
                                        .reduce((prev: any, curr) => { return [...prev, ...curr.microservices] }, [])
                                        .map((microservice: any) => (
                                            <Row gutter={24} style={{ marginBottom: 5 }} key={microservice.name}>
                                                <Col xs={24} key={microservice.name}>
                                                    <Card type="inner">
                                                        <Row gutter={24} align={"middle"} className="apps-items">
                                                            <Col xs={24} sm={12} md={7}>
                                                                <div>
                                                                    <Space>
                                                                        <TemplateAvatar name={microservice.name} size="small" />
                                                                        <div style={{ lineHeight: 1.3 }}>
                                                                            <Link
                                                                                style={{ fontWeight: 500 }}
                                                                                to={`/projects/${projectId}/environments/${envId}/apps/${microservice.kubernetesId}/${microservice.namespace}/${microservice.name}`}
                                                                            >
                                                                                {microservice.name}
                                                                            </Link>
                                                                            <div>
                                                                                <small style={{ color: "gray" }}>last deployed {moment(microservice.lastDeployed).fromNow()}</small>
                                                                            </div>
                                                                        </div>
                                                                    </Space>
                                                                </div>
                                                                <SegmentedItems items={["Namespace", microservice.namespace]} />
                                                            </Col>
                                                            <Col xs={12} sm={5} md={2}>
                                                                {microservice.isHealthy ?
                                                                    <Tag color="success">Active</Tag> :
                                                                    microservice.isPending ?
                                                                        <Tag>Pending</Tag> :
                                                                        <Tag color="error">Failed</Tag>}
                                                            </Col>
                                                            <Col xs={12} sm={7} md={4}>
                                                                <PodsStats {...microservice.podStatus} />
                                                            </Col>
                                                            <Col xs={20} sm={15} md={9}>
                                                                {
                                                                    microservice.commit ?
                                                                        <div style={{ fontSize: "12px" }}>
                                                                            <b>{microservice.commit.message}</b>
                                                                            <div>
                                                                                Commit by <Avatar src={microservice.commit.user.avatar} size={16} /> {microservice.commit.user.name}
                                                                            </div>
                                                                            <div>
                                                                                <GitCommitLink version={microservice.commit.sha || microservice.commit.version} repoUrl={microservice.commit.repository} branch={microservice.commit.name} />
                                                                            </div>
                                                                        </div> :
                                                                        <>No deployment information available</>
                                                                }
                                                            </Col>
                                                            <Col xs={4} sm={9} md={2} style={{ textAlign: "right" }}>
                                                                <Dropdown
                                                                    placement="bottomRight"
                                                                    trigger={["click"]}
                                                                    menu={{
                                                                        items: [
                                                                            {
                                                                                key: "deploy-different-version",
                                                                                label: "Deploy different version", // deploy
                                                                                onClick: () => { setShowDeployDifferentApplicationVersionModal(true); setChosenService(microservice) }
                                                                            },
                                                                            {
                                                                                key: "deploy-same-version",
                                                                                label: "Restart", // redeploy
                                                                                onClick: () => showRestartConfirm(microservice)
                                                                            },
                                                                            {
                                                                                key: "undeploy",
                                                                                label: "Delete", // undeploy
                                                                                danger: true,
                                                                                onClick: () => showDeleteConfirm(microservice)
                                                                            }
                                                                        ]
                                                                    }}
                                                                >
                                                                    <Button icon={<MoreOutlined />} />
                                                                </Dropdown>
                                                            </Col>
                                                        </Row>
                                                    </Card>
                                                </Col>
                                            </Row>
                                        ))
                                }
                            </>
                    }
                    <div style={{ textAlign: "right" }}>
                        Total of {numOfDeployedServices} applications(s) in this environment
                    </div>
                </>
            }
            {

                currentProject?.paymentPlan?.id === "FREE" &&
                <>
                    <br />
                    <CardMarketing
                        title="Deploy your apps on AWS"
                        subtitle="To deploy infrastructure on AWS you need to be on a Starter Plan ($29/month)."
                        description="Create an EKS cluster on your AWS account to deploy your apps and have ownership of your infrastructure and data."
                        action={<Dropdown
                            trigger={["click"]}
                            placement="bottomRight"
                            menu={{
                                direction: "ltr",
                                items: [
                                    {
                                        key: "eks",
                                        label: "Create Amazon EKS",
                                        onClick: () => navigate("/templates/new?template=https://raw.githubusercontent.com/microtica/templates/master/aws-eks/template.yaml", { state: { envId } })
                                    },
                                    {
                                        key: "eks-spot",
                                        label: "Create Amazon EKS Spot",
                                        onClick: () => navigate("/templates/new?template=https://raw.githubusercontent.com/microtica/templates/master/aws-eks-spot/template.yaml", { state: { envId } })
                                    }
                                ]
                            }}
                        >
                            <Button type="primary">
                                Create New Cluster <DownOutlined />
                            </Button>
                        </Dropdown>}
                        image={DeploymentsImage}
                    />
                </>
            }
            {showDeployDifferentApplicationVersionModal &&
                <DeployDifferentApplicationVersionModal
                    visible={showDeployDifferentApplicationVersionModal}
                    service={chosenService as GetDeployedMicroservicesInStageResponseMicroservices}
                    onCancel={() => setShowDeployDifferentApplicationVersionModal(false)}
                    onOk={() => {
                        envDetails.refreshRoot();
                        setShowDeployDifferentApplicationVersionModal(false);
                    }}
                />
            }
        </>
    )
}

export default EnvironmentApps;